import { BffBrandType } from '@/types/generated';

import { BrandThemeConfig } from '@shared-components/types';

const contentfulEnvironmentSuffix = process.env.CONTENTFUL_ENVIRONMENT_ID
	? `/environments/${process.env.CONTENTFUL_ENVIRONMENT_ID}`
	: '';

const CONTENTFUL_GRAPHQL_URL = `https://graphql.contentful.com/content/v1/spaces/${process.env.CONTENTFUL_SPACE_ID}${contentfulEnvironmentSuffix}`;

const config: BrandThemeConfig = {
	name: 'Lovet',
	logo: {
		dark: 'https://images.ctfassets.net/m8zwsu9tyucg/6eLdygMaUQr4ohKZLkM3vP/71c1c7dc0c69b8ef4b1373fb8d39f525/Lovet_rgb_full_lock_up_pos_sm_no-padding.svg',
		light: 'https://images.ctfassets.net/m8zwsu9tyucg/4KkZLxLLdC7Oj8tGlCGxHs/b1c325a46cf773da74d15549dfe4ea65/Lovet_rgb_full_lock_up_neg_sm_no-padding.svg',
	},
	siteURL: 'https://www.lovet.com',
	siteURLStage: 'https://wwwstg.lovet.com',
	services: {
		prod: {
			REVSPRING_API_URL: '/api/rssched/',
			CONTENTFUL_GRAPHQL_URL,
			BEFFE_GRAPHQL_URL: 'https://www.lovet.com/api/bff',
			FORM_EVENTS_URL: 'https://api.teamtag.com/form/events',
			CLEARCHOICE_API_URL: 'https://www.clearchoice.com/api/v1/webapisched',
			GOOGLE_MAP_ID: '38da7512ef94f433',
			GOOGLE_MAP_API_KEY: 'AIzaSyBo2cpQlVzu7MOmcVEqBzEQ9KwdRZ-gLMg',
		},
		nonprod: {
			REVSPRING_API_URL: '/api/rssched/',
			CONTENTFUL_GRAPHQL_URL,
			BEFFE_GRAPHQL_URL: 'https://wwwstg.lovet.com/api/bff',
			FORM_EVENTS_URL: 'https://aspen-userevents.gcp.stage.aspendental.com/form/events',
			CLEARCHOICE_API_URL: 'https://wwwstg.clearchoice.com/api/v1/webapisched',
			GOOGLE_MAP_ID: '350b123288b58d3f',
			GOOGLE_MAP_API_KEY: 'AIzaSyBykgutCtBGhsf4-X-6lDHTi7Rs0xld5K4',
		},
	},
	features: {
		redirects: [],
		rewrite: {},
		favIcons: {},
		sitemap: {
			additionalSitemapIndexes: [
				'https://www.lovet.com/locations/sitemap-index.xml',
				'https://www.lovet.com/book-an-apointment/sitemap-index.xml',
			],
		},
		analytics: {
			enabled: true,
			inline: false,
			vwoAccountId: '680759',
			gtmId: ['prod', 'stage'].includes(process.env.ENVIRONMENT_NAME) ? 'GTM-KD7DWNN' : 'GTM-T8FRD4Q9',
			facebookPixel: false,
			active: true,
			braze: {
				enabled: true,
				sdkKey:
					process.env.ENVIRONMENT_NAME === 'prod'
						? '8158315f-3da7-461b-aadf-dbe656c5b4c0'
						: 'a93ece9e-7f93-441e-a05f-ae86a9b77326',
				restApiBaseUrl: 'https://rest.iad-07.braze.com',
				baseUrl: 'sdk.iad-07.braze.com',
				enableLogging: process.env.ENVIRONMENT_NAME !== 'prod',
			},
		},
		monitoring: {
			bugSnagApiKey: process.env.BUGSNAG_API_KEY || '575c3f1095887b41f64a344c480c1923',
		},
		header: {
			hideHeaderOnScroll: true,
		},
		chat: {
			enabled: false,
			chatBotURL:
				'https://aspendental.amelia.com/Amelia/ui/aspencallcenter/chat?embed=iframe&domainCode=aspencallcenter',
			isIconButton: false,
		},
		notFoundPage: {
			desktopBackgroundImage:
				'https://images.ctfassets.net/m8zwsu9tyucg/AxWVBlTbpeJHO04OOM3SR/4e3ed05d72259d8aeb71d6c6f4bcafdf/WellNow404.png?q=90&fit=fill&f=face&fm=webp',
			mobileBackgroundImage:
				'https://images.ctfassets.net/m8zwsu9tyucg/4NGVlzvau9wEqmZd5h1pBt/0bd600d7d7c0f569d31fa1848b631806/WellNow404_M.jpg?q=90&fit=fill&f=face&fm=webp',
		},
		livePreview: {
			local: 'https://local.lovet.com:8443',
			stage: 'https://wwwstg.lovet.com',
			prod: 'https://www.lovet.com',
		},
		footer: {
			lite: false,
			alt: true,
			withSimpleLayout: false,
		},
		blogs: {
			root: '/blog/',
			useTagLinks: false,
			pageSize: 12,
		},
		migration: {
			paths: ['dental-care-resources'],
		},
		otherApps: {
			facilityDetailsBasePath: 'locations',
			schedulingPath: 'book-an-appointment',
			facilityDetailsBasePathBreadCrumbTitle: 'locations',
		},
		providers: {
			root: '/providers/',
		},
		facilityDetailsDefaultId: 'Default_LV',
		schema: {},
	},
	featureFlags: {
		featureList: [],
		salesForceEvergageScript: 'Off',
		providerHeroTheme: 'dark',
		showHeaderWithLocationNav: true,
		showMobileHeaderLocationBar: true,
		defaultToCdn: false,
		useFacilityBeffeDataSource: false,
		showProviderPageCtaInMeetOurStaff: false,
		loadFacilityReviewsFromBeffe: false,
		showReadMoreForBioInMeetOurStaff: false,
		transitionallyUseBeffe: true,
		useInlineOneTrust: false,
		useSessionStorageFacilityCode: false,
		withLeadingProviderAssociations: false,
		doNotIndexWholeSite: false,
		hideHeaderFacilityLocator: false,
		hideHeaderSchedulingButton: false,
		showHeaderNavigationMenu: false,
		enableProvidersSitemap: false,
		useGoogleMapAutocompleteSearch: false,
		fetchAllLocationsOnEmptyResults: false,
		hideProviderWithNoImage: false,
		useSimpleFindOfficeLink: false,
		enableFluidTypography: false,
		oneTrust: false,
		enableCcMarketingSourceTransformation: false,
		enforceContinentalUsZoom: false,
	},
	schemaUrl: '',
	themeColor: '#FFFFFF',
	contentfulAppId: 'Lovet - Marketing',
	themeId: 'lovet',
	bffBrandType: BffBrandType.Lovet,
	googleMapBusinessQuery: 'https://www.google.com/maps/search/?api=1&query=Lovet Hospitals, [address]',
	defaultCoordinates: {
		latitude: 39.8283,
		longitude: -98.5795,
	},
};

export default config;
